import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Mail,
  Linkedin,
  Brain,
  User,
  Megaphone,
} from "lucide-react";
import {
  useContactDetailFull,
  useContactOutreachMessages,
} from "@/hooks/useContactDetail";
import {
  useContactCampaigns,
  useContactOpportunities,
} from "@/hooks/useContactIntelligence";
import { ContactIntelTab } from "./ContactIntelTab";
import { ContactProfileTab } from "./ContactProfileTab";
import { ContactCampaignsTab } from "./ContactCampaignsTab";
import { openExternal } from "@/lib/utils";

// ── Props ──────────────────────────────────────────────────────────

interface ContactDetailModalProps {
  contactId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fallbackData?: {
    full_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
    job_title?: string | null;
    company_name?: string | null;
    email?: string | null;
    linkedin_url?: string | null;
    location?: string | null;
    headline?: string | null;
  } | null;
}

// ── Helpers ────────────────────────────────────────────────────────

function getInitials(name: string | null): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

// ── Component ──────────────────────────────────────────────────────

export function ContactDetailModal({
  contactId,
  open,
  onOpenChange,
  fallbackData,
}: ContactDetailModalProps) {
  const { data: contact, isLoading } = useContactDetailFull(open ? contactId : null);
  const { data: campaigns } = useContactCampaigns(open ? contactId : null);
  const { data: opportunities } = useContactOpportunities(open ? contactId : null);
  const { data: outreachMessages } = useContactOutreachMessages(open ? contactId : null);

  const [activeTab, setActiveTab] = useState("intel");

  // Reset tab when modal opens
  useEffect(() => {
    if (open) setActiveTab("intel");
  }, [open]);

  // Use contact data if available, fallback otherwise
  const displayName =
    contact?.full_name ||
    fallbackData?.full_name ||
    [fallbackData?.first_name, fallbackData?.last_name].filter(Boolean).join(" ") ||
    "Contact";
  const displayTitle = contact?.job_title || fallbackData?.job_title;
  const displayCompany = contact?.company_name || fallbackData?.company_name;
  const displayEmail = contact?.email || fallbackData?.email;
  const displayLinkedIn = contact?.linkedin_url || fallbackData?.linkedin_url;
  const displayLocation = contact?.location || contact?.clay_location || fallbackData?.location;

  // Unmatched contact: no contactId or contact not found
  const isUnmatched = !contactId || (!isLoading && !contact);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0">
        {isLoading ? (
          <div className="p-6 space-y-4">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b space-y-3">
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <Avatar className="h-12 w-12 shrink-0">
                  {contact?.clay_picture_url && (
                    <AvatarImage src={contact.clay_picture_url} alt={displayName} />
                  )}
                  <AvatarFallback className="text-sm bg-primary/10 text-primary">
                    {getInitials(displayName)}
                  </AvatarFallback>
                </Avatar>

                <div className="space-y-1 min-w-0">
                  <DialogHeader>
                    <DialogTitle className="text-xl">{displayName}</DialogTitle>
                  </DialogHeader>
                  <div className="flex items-center gap-2 flex-wrap">
                    {(contact?.job_title || displayTitle) && (
                      <span className="text-sm text-muted-foreground">
                        {contact?.job_title || displayTitle}
                      </span>
                    )}
                    {(contact?.company_name || displayCompany) && (
                      <>
                        <span className="text-sm text-muted-foreground">at</span>
                        <span className="text-sm font-medium">
                          {contact?.company_name || displayCompany}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {contact?.seniority_normalized && (
                      <Badge variant="outline" className="text-xs">
                        {contact.seniority_normalized}
                      </Badge>
                    )}
                    {contact?.department_normalized && (
                      <Badge variant="secondary" className="text-xs">
                        {contact.department_normalized}
                      </Badge>
                    )}
                    {contact?.clay_enrichment_status === "enriched" && (
                      <Badge
                        variant="outline"
                        className="text-[10px] bg-green-500/10 text-green-700 border-green-500/20"
                      >
                        Clay Enriched
                      </Badge>
                    )}
                    {(contact?.email || displayEmail) && (
                      <a
                        href={`mailto:${contact?.email || displayEmail}`}
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail className="h-3.5 w-3.5" />
                      </a>
                    )}
                    {(contact?.linkedin_url || displayLinkedIn) && (
                      <button
                        type="button"
                        onClick={() => openExternal(contact?.linkedin_url || displayLinkedIn)}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <Linkedin className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {(contact?.location || displayLocation) && (
                      <span className="text-xs text-muted-foreground">
                        {contact?.location || displayLocation}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            {isUnmatched ? (
              <div className="flex-1 overflow-auto p-6">
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <User className="h-10 w-10 mb-3" />
                  <p className="text-sm font-medium">Unmatched Contact</p>
                  <p className="text-xs mt-1 text-center max-w-sm">
                    This contact hasn't been matched to a contact record yet.
                    Only basic campaign data is available.
                  </p>
                </div>
              </div>
            ) : contact && (
              <Tabs
                value={activeTab}
                onValueChange={setActiveTab}
                className="flex-1 flex flex-col min-h-0"
              >
                <TabsList className="mx-6 mt-2 w-fit">
                  <TabsTrigger value="intel" className="text-xs">
                    <Brain className="h-3.5 w-3.5 mr-1.5" />
                    Intel
                  </TabsTrigger>
                  <TabsTrigger value="profile" className="text-xs">
                    <User className="h-3.5 w-3.5 mr-1.5" />
                    Profile
                  </TabsTrigger>
                  <TabsTrigger value="campaigns" className="text-xs">
                    <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                    Campaigns
                    {(campaigns?.length ?? 0) > 0 && (
                      <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                        {campaigns?.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <div className="flex-1 min-h-0">
                  {/* ── Tab 1: Intel ─────────────────────────────────── */}
                  <TabsContent value="intel" className="mt-0 flex-1 min-h-0">
                    <ScrollArea className="h-[calc(90vh-280px)]">
                      <div className="px-6 pb-6 pt-4">
                        <ContactIntelTab contact={contact} />
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* ── Tab 2: Profile (Clay Enrichment) ─────────────── */}
                  <TabsContent value="profile" className="mt-0 flex-1 min-h-0">
                    <ScrollArea className="h-[calc(90vh-280px)]">
                      <div className="px-6 pb-6 pt-4">
                        <ContactProfileTab contact={contact} />
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  {/* ── Tab 3: Campaigns & Outreach ──────────────────── */}
                  <TabsContent value="campaigns" className="mt-0 flex-1 min-h-0">
                    <ScrollArea className="h-[calc(90vh-280px)]">
                      <div className="px-6 pb-6 pt-4">
                        <ContactCampaignsTab
                          campaigns={campaigns}
                          opportunities={opportunities}
                          outreachMessages={outreachMessages}
                        />
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </div>
              </Tabs>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
